import { useState, useEffect, useCallback } from 'react';

export interface RecentTextItem {
  id: string;
  text: string;
  count: number;
  lastUsed: number;
  firstAdded: number;
}

interface RecentTextStorage {
  version: 1;
  items: RecentTextItem[];
  maxItems: number;
}

interface UseRecentlyUsedOptions {
  storageKey: string;
  maxItems?: number;
  deduplicate?: boolean;
}

interface UseRecentlyUsedReturn {
  items: RecentTextItem[];
  addItem: (text: string) => void;
  removeItem: (id: string) => void;
  clearAll: () => void;
  hasItems: boolean;
}

function normalizeText(text: string): string {
  return text
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 100);
}

function getRecencyFactor(lastUsed: number): number {
  const hoursSinceUse = (Date.now() - lastUsed) / (1000 * 60 * 60);
  if (hoursSinceUse < 24) return 1.0;
  if (hoursSinceUse < 168) return 0.8; // 1 week
  return 0.5;
}

function sortItems(items: RecentTextItem[]): RecentTextItem[] {
  return [...items].sort((a, b) => {
    const scoreA = a.count * getRecencyFactor(a.lastUsed);
    const scoreB = b.count * getRecencyFactor(b.lastUsed);
    return scoreB - scoreA;
  });
}

function loadFromStorage(storageKey: string, maxItems: number): RecentTextStorage {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return { version: 1, items: [], maxItems };
    }

    const parsed = JSON.parse(stored);

    // Migration from old format
    if (!parsed.version) {
      return {
        version: 1,
        items: Array.isArray(parsed) ? parsed : [],
        maxItems
      };
    }

    return parsed;
  } catch (e) {
    console.error('Failed to load recent items:', e);
    return { version: 1, items: [], maxItems };
  }
}

function saveToStorage(storageKey: string, storage: RecentTextStorage): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(storage));
  } catch (e) {
    if (e instanceof Error && e.name === 'QuotaExceededError') {
      // Remove oldest items and retry
      const reduced: RecentTextStorage = {
        ...storage,
        items: storage.items.slice(0, Math.floor(storage.items.length / 2))
      };
      try {
        localStorage.setItem(storageKey, JSON.stringify(reduced));
      } catch {
        console.error('Failed to save recent items even after reducing');
      }
    }
  }
}

export function useRecentlyUsed({
  storageKey,
  maxItems = 8,
  deduplicate = true
}: UseRecentlyUsedOptions): UseRecentlyUsedReturn {
  const [items, setItems] = useState<RecentTextItem[]>(() => {
    const storage = loadFromStorage(storageKey, maxItems);
    return sortItems(storage.items);
  });

  // Cross-tab synchronization
  useEffect(() => {
    function handleStorageChange(e: StorageEvent) {
      if (e.key === storageKey && e.newValue) {
        try {
          const parsed: RecentTextStorage = JSON.parse(e.newValue);
          setItems(sortItems(parsed.items));
        } catch {
          // Ignore invalid data
        }
      }
    }

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey]);

  const addItem = useCallback((text: string) => {
    const normalized = normalizeText(text);
    if (!normalized) return;

    setItems(currentItems => {
      let updatedItems = [...currentItems];

      if (deduplicate) {
        // Check for duplicate (case-insensitive)
        const existingIndex = updatedItems.findIndex(
          item => item.text.toLowerCase() === normalized.toLowerCase()
        );

        if (existingIndex !== -1) {
          // Update existing item
          const existing = updatedItems[existingIndex];
          updatedItems[existingIndex] = {
            ...existing,
            count: existing.count + 1,
            lastUsed: Date.now()
          };
        } else {
          // Add new item
          const newItem: RecentTextItem = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: normalized,
            count: 1,
            lastUsed: Date.now(),
            firstAdded: Date.now()
          };
          updatedItems.unshift(newItem);
        }
      } else {
        // Always add as new item
        const newItem: RecentTextItem = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          text: normalized,
          count: 1,
          lastUsed: Date.now(),
          firstAdded: Date.now()
        };
        updatedItems.unshift(newItem);
      }

      // Trim to max items
      updatedItems = updatedItems.slice(0, maxItems);

      // Sort by score
      const sorted = sortItems(updatedItems);

      // Save to storage
      saveToStorage(storageKey, {
        version: 1,
        items: sorted,
        maxItems
      });

      return sorted;
    });
  }, [storageKey, maxItems, deduplicate]);

  const removeItem = useCallback((id: string) => {
    setItems(currentItems => {
      const filtered = currentItems.filter(item => item.id !== id);

      saveToStorage(storageKey, {
        version: 1,
        items: filtered,
        maxItems
      });

      return filtered;
    });
  }, [storageKey, maxItems]);

  const clearAll = useCallback(() => {
    setItems([]);
    saveToStorage(storageKey, {
      version: 1,
      items: [],
      maxItems
    });
  }, [storageKey, maxItems]);

  return {
    items,
    addItem,
    removeItem,
    clearAll,
    hasItems: items.length > 0
  };
}
